import { useState } from 'react';

export default function TDSCalculator() {
  const [expense, setExpense] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState('');

  const handleCalculate = async () => {
    if (!expense.trim()) { setError('Please enter an expense type'); return; }
    setLoading(true);
    setError('');
    setResult(null);

    try {
      const response = await fetch('/api/tds/calculate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ expense_description: expense }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Calculation failed');
      setResult(data);
    } catch (err: any) {
      setError(err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const examples = [
    'Payment to contractor for construction work',
    'Professional fees to a lawyer',
    'Rent paid for office premises',
    'Commission to sales agent',
    'Interest on loan from bank',
  ];

  return (
    <div className="max-w-3xl mx-auto px-4 py-12">
      <div className="text-center mb-10">
        <span className="text-5xl">🧮</span>
        <h1 className="text-3xl font-bold text-gray-800 mt-4">
          TDS Calculator
        </h1>
        <p className="text-gray-500 mt-3 max-w-xl mx-auto">
          Enter any expense description and our AI will identify the applicable
          TDS section, rate, and threshold limit under the Income Tax Act 1961.
        </p>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border p-8">
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Describe the expense or payment
        </label>
        <textarea
          value={expense}
          onChange={e => setExpense(e.target.value)}
          placeholder="e.g. Payment to a contractor for civil construction work at our office..."
          className="w-full border border-gray-200 rounded-xl p-4 text-gray-700 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
          rows={4}
        />

        <div className="mt-3">
          <p className="text-xs text-gray-400 mb-2">Quick examples:</p>
          <div className="flex flex-wrap gap-2">
            {examples.map((ex, i) => (
              <button
                key={i}
                onClick={() => setExpense(ex)}
                className="text-xs bg-gray-100 hover:bg-blue-50 hover:text-blue-700 text-gray-600 px-3 py-1 rounded-full transition">
                {ex}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        <button
          onClick={handleCalculate}
          disabled={!expense.trim() || loading}
          className="w-full mt-6 bg-blue-700 text-white py-3 rounded-xl font-semibold hover:bg-blue-800 transition disabled:opacity-50 text-lg">
          {loading ? 'Calculating...' : 'Find TDS Section & Rate'}
        </button>

        {result && (
          <div className="mt-6 bg-gray-50 rounded-xl p-6 border">
            <h3 className="font-bold text-gray-800 text-lg mb-4">TDS Details</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white rounded-lg p-4 border">
                <p className="text-xs text-gray-400 uppercase font-semibold">Section</p>
                <p className="text-xl font-bold text-blue-700 mt-1">
                  {result.section || 'N/A'}
                </p>
              </div>
              <div className="bg-white rounded-lg p-4 border">
                <p className="text-xs text-gray-400 uppercase font-semibold">TDS Rate</p>
                <p className="text-xl font-bold text-blue-700 mt-1">
                  {result.rate || 'N/A'}
                </p>
              </div>
              <div className="bg-white rounded-lg p-4 border">
                <p className="text-xs text-gray-400 uppercase font-semibold">Threshold Limit</p>
                <p className="text-lg font-semibold text-gray-700 mt-1">
                  {result.threshold || 'N/A'}
                </p>
              </div>
              <div className="bg-white rounded-lg p-4 border">
                <p className="text-xs text-gray-400 uppercase font-semibold">Without PAN</p>
                <p className="text-lg font-semibold text-gray-700 mt-1">
                  {result.rate_without_pan || 'N/A'}
                </p>
              </div>
            </div>
            {result.notes && (
              <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">
                  <span className="font-semibold">Note:</span> {result.notes}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
